import React, { useState } from 'react';
import { Package, Users, Plus, UserPlus, Eye, Trash2, MessageCircle, Check, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Contract, FuncionarioEscalado } from '../../../types/contract';

import { EscalarFuncionarioSidebar } from './EscalarFuncionarioSidebar';
import { formatCurrency } from '../../../utils/currencyUtils';
import { ComponentHeader } from './ComponentHeader';
import { contractService } from '../../../services/contractService';
import { employeeService } from '../../../services/employeeService';
import { useContracts } from '../../../contexts/ContractContext';

interface PackagePaymentCardProps {
  contract: Contract;
}

export const PackagePaymentCard: React.FC<PackagePaymentCardProps> = ({ contract }) => {
  const navigate = useNavigate();
  const { updateContract } = useContracts();
  const [showEscalarSidebar, setShowEscalarSidebar] = useState(false);
  const [funcionariosEscalados, setFuncionariosEscalados] = useState<FuncionarioEscalado[]>(contract.funcionarios || []);
  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);

  const handleTogglePaymentStatus = async () => {
    if (!contract.id) return;
    
    setIsUpdatingPayment(true);
    try {
      const newPaymentStatus = !contract.pago;
      
      // Atualizar no backend
      await contractService.updateContract(contract.id, { pago: newPaymentStatus });
      
      // Atualizar no contexto global
      updateContract(contract.id, { pago: newPaymentStatus });
      
      console.log(`✅ Status de pagamento alterado para: ${newPaymentStatus ? 'Pago' : 'Pendente'}`);
    } catch (error) {
      console.error('❌ Erro ao alterar status de pagamento:', error);
      alert('Erro ao alterar status de pagamento. Tente novamente.');
    } finally {
      setIsUpdatingPayment(false);
    }
  };

  const handleEscalarFuncionario = async (funcionarioData: Omit<FuncionarioEscalado, 'id'>) => {
    const funcionarioEscalado: FuncionarioEscalado = {
      id: Date.now().toString(),
      ...funcionarioData
    };

    try {
      // Adicionar à lista local primeiro (para feedback imediato)
      const novaListaFuncionarios = [...funcionariosEscalados, funcionarioEscalado];
      setFuncionariosEscalados(novaListaFuncionarios);
      
      // Salvar no Firebase
      if (contract.id) {
        await contractService.updateContract(contract.id, {
          funcionarios: novaListaFuncionarios
        });
        console.log('✅ Funcionário escalado e salvo no Firebase:', funcionarioEscalado);
      } else {
        console.warn('⚠️ Contract ID não encontrado, funcionário escalado apenas localmente');
      }
    } catch (error) {
      console.error('❌ Erro ao salvar funcionário escalado:', error);
      // Reverter mudança local em caso de erro
      setFuncionariosEscalados(funcionariosEscalados);
      alert('Erro ao escalar funcionário. Tente novamente.');
    }
  };

  const handleRemoverFuncionario = async (funcionarioId: string) => {
    try {
      // Remover da lista local primeiro (para feedback imediato)
      const novaListaFuncionarios = funcionariosEscalados.filter(func => func.id !== funcionarioId);
      setFuncionariosEscalados(novaListaFuncionarios);
      
      // Salvar no Firebase
      if (contract.id) {
        await contractService.updateContract(contract.id, {
          funcionarios: novaListaFuncionarios
        });
        console.log('✅ Funcionário removido e salvo no Firebase');
      } else {
        console.warn('⚠️ Contract ID não encontrado, funcionário removido apenas localmente');
      }
    } catch (error) {
      console.error('❌ Erro ao remover funcionário:', error);
      // Reverter mudança local em caso de erro
      setFuncionariosEscalados(funcionariosEscalados);
      alert('Erro ao remover funcionário. Tente novamente.');
    }
  };

  const handleEnviarLembreteWhatsApp = async (funcionario: FuncionarioEscalado) => {
    try {
      // Buscar informações do funcionário no sistema para obter o telefone
      let telefone = '11999999999'; // Telefone padrão
      
      if (funcionario.funcionarioId) {
        const funcionarioCompleto = await employeeService.getEmployeeById(funcionario.funcionarioId);
        if (funcionarioCompleto && funcionarioCompleto.telefone) {
          telefone = funcionarioCompleto.telefone;
        }
      }
      
      // Formatear a data do evento usando a mesma lógica do contrato
      console.log('🔍 Debug dataFesta:', contract.dataFesta, 'Tipo:', typeof contract.dataFesta);
      
      let dataEvento = 'Data a definir';
      if (contract.dataFesta) {
        try {
          let dateObj;
          
          // Se a data está em formato brasileiro (DD/MM/YYYY)
          if (contract.dataFesta.includes('/')) {
            const [day, month, year] = contract.dataFesta.split('/');
            // Criar data local sem conversão de fuso horário
            dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          } else if (contract.dataFesta.includes('-')) {
            // Se a data está em formato ISO (YYYY-MM-DD)
            dateObj = new Date(contract.dataFesta + 'T00:00:00');
          } else {
            // Outros formatos
            dateObj = new Date(contract.dataFesta);
          }
          
          // Verificar se a data é válida
          if (!isNaN(dateObj.getTime())) {
            const dia = dateObj.getDate();
            const mes = dateObj.toLocaleString('pt-BR', { month: 'long' });
            const ano = dateObj.getFullYear();
            dataEvento = `${dia} de ${mes} de ${ano}`;
            console.log('✅ Data formatada para WhatsApp:', dataEvento);
          } else {
            console.warn('⚠️ Data inválida:', contract.dataFesta);
            dataEvento = 'Data a definir';
          }
        } catch (error) {
          console.error('❌ Erro ao processar data:', error);
          dataEvento = 'Data a definir';
        }
      }
      
      // Criar mensagem personalizada
      const mensagem = `🎉 *Lembrete de Evento - PlayKids*

Olá ${funcionario.nome}! 

Você está escalado(a) para o evento:
📅 *Data:* ${dataEvento}
👤 *Aniversariante:* ${contract.nomeAniversariante}
🎯 *Sua Função:* ${funcionario.funcao}
💰 *Valor:* ${formatCurrency(funcionario.valorPagamento.toString())}

📍 *Local:* ${contract.endereco || 'Endereço a confirmar'}

${funcionario.observacoes ? `📝 *Observações:* ${funcionario.observacoes}` : ''}

Por favor, confirme sua presença! 

Qualquer dúvida, entre em contato conosco.

Att,
Equipe PlayKids`;

      // Codificar a mensagem para URL
      const mensagemCodificada = encodeURIComponent(mensagem);
      
      // Criar URL do WhatsApp
      const urlWhatsApp = `https://wa.me/55${telefone.replace(/\D/g, '')}?text=${mensagemCodificada}`;
      
      // Abrir WhatsApp em nova aba
      window.open(urlWhatsApp, '_blank');
      
      console.log('✅ Lembrete enviado via WhatsApp para:', funcionario.nome);
    } catch (error) {
      console.error('❌ Erro ao enviar lembrete:', error);
      alert('Erro ao enviar lembrete. Verifique se o funcionário tem telefone cadastrado.');
    }
  };



  return (
    <>
      <div className="bg-white shadow-lg border border-slate-200 rounded-lg overflow-hidden">
        <ComponentHeader
          title="Pacote & Pagamento"
          subtitle={contract.pacoteAtividades || 'PERSONALIZADO'}
          icon={Package}
        />
        <div className="p-2 space-y-1.5">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-500 w-16">Pacote:</span>
            <span className="font-medium">{contract.pacoteAtividades || 'PERSONALIZADO'}</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-500 w-16">Valor:</span>
            {(!contract.valor || contract.valor.trim().toUpperCase() === 'N/A') ? (
              <span className="font-medium text-slate-500">Aguardando Orçamento</span>
            ) : (
              <span className="font-medium text-emerald-600">{contract.valor ? formatCurrency(contract.valor) : 'N/A'}</span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-500 w-16">Status:</span>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                contract.pago 
                  ? 'bg-emerald-500 text-white border border-emerald-600 shadow-sm' 
                  : 'bg-amber-500 text-white border border-amber-600 shadow-sm'
              }`}>
                {contract.pago ? 'Pago' : 'Pendente'}
              </span>
              <button
                onClick={handleTogglePaymentStatus}
                disabled={isUpdatingPayment}
                className={`p-1 rounded transition-colors ${
                  contract.pago
                    ? 'text-red-500 hover:text-red-700 hover:bg-red-50'
                    : 'text-green-500 hover:text-green-700 hover:bg-green-50'
                } ${isUpdatingPayment ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={contract.pago ? 'Marcar como Pendente' : 'Marcar como Pago'}
              >
                {isUpdatingPayment ? (
                  <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                ) : contract.pago ? (
                  <X className="w-3 h-3" />
                ) : (
                  <Check className="w-3 h-3" />
                )}
              </button>
            </div>
          </div>
          {contract.temaFesta && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-slate-500 w-16">Tema:</span>
              <span className="font-medium">{contract.temaFesta}</span>
            </div>
          )}
          
          {/* Seção de Funcionários */}
          <div className="border-t border-slate-100 pt-2 mt-2">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-500" />
                <span className="text-xs font-medium text-slate-700">Funcionários Escalados</span>
              </div>
              <button
                onClick={() => setShowEscalarSidebar(true)}
                className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                title="Escalar funcionário"
              >
                <Plus className="w-3 h-3" />
              </button>
            </div>
            
            {funcionariosEscalados.length === 0 ? (
              <div className="text-center py-3">
                <div className="text-slate-400 mb-2">
                  <Users className="w-8 h-8 mx-auto" />
                </div>
                <p className="text-xs text-slate-500 mb-2">Nenhum funcionário escalado</p>
                <button
                  onClick={() => setShowEscalarSidebar(true)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-600 bg-slate-100 hover:bg-slate-200 rounded border border-slate-200 transition-colors"
                >
                  <UserPlus className="w-3 h-3" />
                  Escalar
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {funcionariosEscalados.map((funcionario) => (
                  <div key={funcionario.id} className="bg-white rounded p-2 border border-slate-200 hover:shadow-sm transition-all">
                    {/* Header com nome, função e valor */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-primary/10 rounded-full flex items-center justify-center">
                          <span className="text-xs font-semibold text-primary">
                            {funcionario.nome.split(' ').map(n => n[0]).join('').toUpperCase()}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs font-semibold text-slate-800">{funcionario.nome}</span>
                          <span className="text-xs text-slate-500">{funcionario.funcao}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-bold text-emerald-600">
                          {formatCurrency(funcionario.valorPagamento.toString())}
                        </span>
                        <button
                          onClick={() => handleEnviarLembreteWhatsApp(funcionario)}
                          className="p-0.5 text-green-500 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
                          title="Enviar lembrete via WhatsApp"
                        >
                          <MessageCircle className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => navigate(`/admin/funcionarios/${funcionario.funcionarioId}`)}
                          className="p-0.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                          title="Ver detalhes do funcionário"
                        >
                          <Eye className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => handleRemoverFuncionario(funcionario.id)}
                          className="p-0.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Remover funcionário"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Observações (se houver) */}
                    {funcionario.observacoes && (
                      <div className="mt-1.5 p-1.5 bg-slate-50 rounded border-l-2 border-slate-200">
                        <p className="text-xs text-slate-600 leading-relaxed">
                          {funcionario.observacoes}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

            {/* Sidebar para Escalar Funcionário */}
      <EscalarFuncionarioSidebar
        isOpen={showEscalarSidebar}
        onClose={() => setShowEscalarSidebar(false)}
        onEscalar={handleEscalarFuncionario}
      />
    </>
  );
};
